import React, { useEffect, useState } from "react";
import { Alert, Button, Dropdown } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

const pmEngagementStatusOptions = ["Completed", "Not Done", "Incomplete", "Ongoing"];

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
}

function getPMEngagementStatusBadgeClass(status) {
  switch (status) {
    case "Completed":
      return "status-badge status-badge-completed";
    case "Incomplete":
      return "status-badge status-badge-incomplete";
    case "Not Done":
      return "status-badge status-badge-not-done";
    case "Ongoing":
    default:
      return "status-badge status-badge-ongoing";
  }
}

function getPMEngagementStatusOptionClass(status) {
  switch (status) {
    case "Completed":
      return "text-success";
    case "Incomplete":
      return "text-warning";
    case "Not Done":
      return "text-danger";
    case "Ongoing":
    default:
      return "text-primary";
  }
}

function PMVisitorLogDetailPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLog();
  }, [uuid]);

  async function fetchLog() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/pm-visitor-logs/${uuid}`);
      setLog(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load PM visitor log details.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);

    try {
      await api.delete(`/pm-visitor-logs/${uuid}`);
      toast.success("PM visitor log deleted");
      history.push("/pm-visitor-logs");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete PM visitor log.");
      setDeleting(false);
    }
  }

  async function handleStatusChange(nextStatus) {
    if (!log || !nextStatus || nextStatus === log.status) {
      return;
    }

    setStatusSaving(true);

    try {
      await api.put(`/pm-visitor-logs/${uuid}`, {
        date: log.date,
        status: nextStatus,
        team_details: log.team_details,
        objective_of_meeting: log.objective_of_meeting,
        action_point: log.action_point,
        responsible_person: log.responsible_person,
        date_due: log.date_due
      });
      toast.success(`PM engagement marked as ${nextStatus}`);
      await fetchLog();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update PM engagement status.");
    } finally {
      setStatusSaving(false);
    }
  }

  function renderDetailItem(label, value) {
    return {
      label,
      value: value ?? "-"
    };
  }

  function renderDetailRows(items) {
    const rows = [];

    for (let index = 0; index < items.length; index += 2) {
      const left = items[index];
      const right = items[index + 1];

      rows.push(
        <tr key={left.label}>
          <th>{left.label}</th>
          <td>{left.value}</td>
          {right ? (
            <>
              <th>{right.label}</th>
              <td>{right.value}</td>
            </>
          ) : (
            <>
              <th className="staff-task-detail-empty"></th>
              <td className="staff-task-detail-empty"></td>
            </>
          )}
        </tr>
      );
    }

    return rows;
  }

  return (
    <div>
      <PageHeader
        title="PM Engagement Log Details"
        action={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => history.push("/pm-visitor-logs")}>
              Back to PM engagement log
            </Button>
            <Button variant="primary" onClick={() => history.push(`/pm-visitor-logs/${uuid}/edit`)}>
              Edit log entry
            </Button>
            <Button variant="outline-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete log entry"}
            </Button>
          </div>
        }
      />

      {loading ? <LoadingState label="Loading PM visitor log..." /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && log ? (
        <div className="d-flex flex-column gap-4">
          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Core details</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 staff-task-detail-table">
                <tbody>
                  {renderDetailRows([
                    renderDetailItem("Date", log.date || "-"),
                    renderDetailItem(
                      "Status",
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className={getPMEngagementStatusBadgeClass(log.status)}>{log.status || "Ongoing"}</span>
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="link"
                            size="sm"
                            className="d-inline-flex align-items-center justify-content-center p-0 border-0 text-muted shadow-none"
                            disabled={statusSaving}
                            id="pm-engagement-status-menu"
                            title="Change status"
                            aria-label="Open PM engagement status settings"
                          >
                            <i className="bi bi-gear"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {pmEngagementStatusOptions.map((status) => (
                              <Dropdown.Item
                                key={status}
                                active={status === log.status}
                                onClick={() => handleStatusChange(status)}
                                className="d-flex align-items-center justify-content-between gap-3"
                              >
                                <span className="d-inline-flex align-items-center gap-2">
                                  <i className={`bi bi-circle-fill small ${getPMEngagementStatusOptionClass(status)}`}></i>
                                  <span>{status}</span>
                                </span>
                                {status === log.status ? <i className="bi bi-check2"></i> : null}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        {statusSaving ? <span className="small text-muted">Updating...</span> : null}
                      </div>
                    ),
                    renderDetailItem("Date due", log.date_due || "-"),
                    renderDetailItem("Responsible person", log.responsible_person || "-"),
                    renderDetailItem("Created", formatDate(log.created_at)),
                    renderDetailItem("Last updated", formatDate(log.last_updated))
                  ])}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Team details</h5>
            <p className="mb-0 text-muted">{log.team_details || "No team details provided."}</p>
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Objective of meeting</h5>
            <p className="mb-0 text-muted">{log.objective_of_meeting || "No objective of meeting provided."}</p>
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Action point</h5>
            <p className="mb-0 text-muted">{log.action_point || "No action point recorded."}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PMVisitorLogDetailPage;
